import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { CheckCircle2, XCircle, AlertCircle, Play } from "lucide-react";
import RulesService from "../../../_services/RulesService";

interface Props {
  ruleId: number | null;
}

interface TraceNode {
  type: string;
  matched: boolean | null;
  detail?: string;
  children?: TraceNode[];
}

interface DryRunResult {
  matched: boolean;
  error?: string;
  trace: TraceNode;
  ruleName: string | null;
}

/**
 * Backs the "Dry run" pane in the rule editor / detail. Takes a student
 * enquiry ID and shows the per-clause trace so admins can see why a rule
 * does or doesn't match for that specific student — without persisting.
 */
const DryRunPanel: React.FC<Props> = ({ ruleId }) => {
  const [enquiryId, setEnquiryId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<DryRunResult | null>(null);

  const run = async () => {
    if (!ruleId) {
      setErr("Save the rule first before running dry-run.");
      return;
    }
    const eid = Number(enquiryId);
    if (!eid) {
      setErr("Enter a numeric Enquiry ID.");
      return;
    }
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const res: any = await RulesService.dryRun(ruleId, eid);
      setResult(res?.data?.data as DryRunResult);
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message || e?.message || "Dry-run failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bm-dry-run">
      <div className="bm-dry-run-input">
        <Form.Control
          size="sm"
          type="number"
          value={enquiryId}
          onChange={(e) => setEnquiryId(e.target.value)}
          placeholder="Enquiry ID"
          disabled={busy}
        />
        <Button variant="primary" size="sm" onClick={run} disabled={busy || !ruleId}>
          <Play size={12} /> {busy ? "Running…" : "Run"}
        </Button>
      </div>

      {!ruleId && (
        <div className="bm-dry-run-hint">Save the rule once before you can dry-run it.</div>
      )}

      {err && <div className="bm-error" style={{ marginTop: 8 }}>{err}</div>}

      {result && (
        <div className="bm-dry-run-result">
          <div
            className={`bm-dry-run-verdict ${result.error ? "is-error" : result.matched ? "is-match" : "is-nomatch"}`}
          >
            {result.error ? (
              <>
                <AlertCircle size={14} /> Evaluation error
              </>
            ) : result.matched ? (
              <>
                <CheckCircle2 size={14} /> Matched — would issue badge
              </>
            ) : (
              <>
                <XCircle size={14} /> Did not match — would not issue
              </>
            )}
          </div>
          <Trace node={result.trace} depth={0} />
        </div>
      )}
    </div>
  );
};

const Trace: React.FC<{ node: TraceNode; depth: number }> = ({ node, depth }) => {
  const isComposite = node.children && node.children.length > 0;
  const verdict =
    node.matched === null ? "error" : node.matched ? "pass" : "fail";
  return (
    <div className="bm-trace-node" style={{ marginLeft: depth * 12 }}>
      <div className={`bm-trace-row is-${verdict}`}>
        <span className="bm-trace-icon">
          {verdict === "pass" ? "✓" : verdict === "fail" ? "✗" : "!"}
        </span>
        <span className="bm-trace-type">{node.type}</span>
        {node.detail && <span className="bm-trace-detail">{node.detail}</span>}
      </div>
      {isComposite &&
        node.children!.map((c, i) => <Trace key={i} node={c} depth={depth + 1} />)}
    </div>
  );
};

export default DryRunPanel;
